import React, { useCallback, useContext, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  Logo,
  SpinnerButton,
  Text,
  TextAlign,
  TextHighlight
} from '@focaldata/cin-ui-components'
import { PfmContext, PfmData } from 'containers/PageFlowManager/PageFlowManager'
import { AppState, AppStateContext } from 'containers/State/AppState'
import useCreateRespondent from 'hooks/useCreateRespondent'
import InfoPageLayout from 'layouts/InfoPageLayout/InfoPageLayout'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import useQueryParams from 'hooks/useQueryParams'

const InfoPage: React.FC = () => {
  const { next } = useContext<PfmData>(PfmContext)
  const {
    respondentProgress: [progress, setProgress]
  } = useContext<AppState>(AppStateContext)
  const [createRespondentLoading, setCreateRespondentLoading] = useState(false)
  const [createRespondentError, setCreateRespondentError] = useState(false)
  const createRespondent = useCreateRespondent()

  const showRecaptchaBranding = !!process.env.REACT_APP_RECAPTCHA_KEY
  const [recaptchaToken, setRecaptchaToken] = useState<string>()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const queryParams = useQueryParams()
  const isFdChat = queryParams.get('type') === 'fdchat'

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return
    }

    const token = await executeRecaptcha('login')

    if (token) {
      setRecaptchaToken(token)
    }
  }, [executeRecaptcha])

  const handleClick: () => Promise<void> = async () => {
    setCreateRespondentLoading(true)
    setCreateRespondentError(false)
    try {
      const existingStatus = await createRespondent(recaptchaToken)

      // If createRespondent returns a status, the respondent already exists (409).
      // End the interview based on their existing status.
      if (existingStatus && existingStatus !== 'inprogress') {
        if (progress) {
          // Mark as returning respondent with existing status to skip quality check
          const newProgress = {
            ...progress,
            isTermsAgreed: true,
            hasExistingStatus: true
          }

          switch (existingStatus) {
            case 'screenout':
              newProgress.isScreened = true
              break
            case 'complete':
              newProgress.isCompleted = true
              break
            case 'quotafull':
              newProgress.isQuotaFull = true
              break
            default:
              newProgress.isQualityTerminated = true
              break
          }

          setProgress(newProgress)
        }
        return
      }

      next()
    } catch {
      // createRespondent already logged "Network Failure: Create respondent failed".
      // Surface an inline error so the click isn't silent. The button stays
      // enabled (for the transient-hiccup case) but we don't prompt "try
      // again" — axios-retry already burned 3 attempts with backoff, so a
      // connection hint is more honest than asking them to mash the button.
      setCreateRespondentError(true)
    } finally {
      setCreateRespondentLoading(false)
    }
  }

  // You can use useEffect to trigger the verification as soon as the component being loaded
  useEffect(() => {
    handleReCaptchaVerify()
  }, [handleReCaptchaVerify])

  return (
    <InfoPageLayout
      visual={<Logo width={180} />}
      title={
        <FormattedMessage
          id="infoPage_welcome"
          defaultMessage="Welcome to your questionnaire brought to you by focaldata."
        />
      }
      subtitle={
        <FormattedMessage
          id={isFdChat ? 'infoPage_fdchatDescription' : 'infoPage_pleaseClick'}
          defaultMessage="Please click the button below to get started. All answers will be confidential."
          values={{
            nl: <br />
          }}
        />
      }
      action={
        <>
          <SpinnerButton
            onClick={handleClick}
            loading={createRespondentLoading}
          >
            <FormattedMessage
              id="infoPage_getStarted"
              defaultMessage="Get started"
            />
          </SpinnerButton>
          {createRespondentError && (
            <Text highlight={TextHighlight.Error} align={TextAlign.Center}>
              <FormattedMessage
                id="infoPage_createRespondentError"
                defaultMessage="Something went wrong. Please check your internet connection."
              />
            </Text>
          )}
        </>
      }
      showRecaptchaBranding={showRecaptchaBranding}
    />
  )
}

export default InfoPage
